/**
 * UTM / Angle Capture — GTM Attribution
 *
 * Captures the acquisition angle from URL params on landing load.
 * Persists in localStorage so it survives navigation to /auth and /onboarding.
 *
 * Storage key: m360:source_angle
 * Values: angle slug codes (see ANGLE_SLUGS) or "organic" (no param)
 *
 * Also captures full UTM params for richer attribution.
 * Storage key: m360:utm_params
 */

import { isCookieConsentAccepted } from "@/lib/cookieConsent";

const SOURCE_ANGLE_KEY = "m360:source_angle";
const UTM_PARAMS_KEY = "m360:utm_params";
const ORIGIN_PARAMS_KEY = "m360:origin_params";
const FBCLID_KEY = "m360:fbclid";
const FBCLID_TS_KEY = "m360:fbclid_ts";
const GCLID_KEY = "m360:gclid";
const GCLID_SOURCE_KEY = "m360:gclid_source";
const GCLID_TS_KEY = "m360:gclid_ts";
/** Ordem de precedência: `gclid` é o valor completo; os braid são o substituto. */
const GCLID_KEYS = ["gclid", "gbraid", "wbraid"] as const;
const VISITOR_UTM_KEY = "m360_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/* ── Angle taxonomy (9 emotional angles + legacy aliases) ── */

/** Canonical angle slugs used across the platform */
export const ANGLE_SLUGS = [
  "sobrecarga",
  "culpa",
  "identidade",
  "exaustao",
  "comparacao",
  "autocobranca",
  "invisibilidade",
  "rotina",
  "leveza",
] as const;

export type AngleSlug = (typeof ANGLE_SLUGS)[number];

/** Human-readable labels for admin/analytics */
export const ANGLE_LABELS: Record<AngleSlug, string> = {
  sobrecarga: "Sobrecarga",
  culpa: "Culpa materna",
  identidade: "Perda de identidade",
  exaustao: "Exaustão",
  comparacao: "Comparação",
  autocobranca: "Autocobrança",
  invisibilidade: "Invisibilidade",
  rotina: "Rotina sem fim",
  leveza: "Leveza",
};

/**
 * Map multiple incoming values → canonical angle slug.
 * Supports: ?angle=X, ?utm_content=X, and legacy short codes (a, b, c).
 */
const ANGLE_ALIASES: Record<string, AngleSlug> = {
  // Legacy short codes
  a: "sobrecarga",
  b: "culpa",
  c: "identidade",
  // Full slugs (canonical)
  sobrecarga: "sobrecarga",
  culpa: "culpa",
  identidade: "identidade",
  exaustao: "exaustao",
  comparacao: "comparacao",
  autocobranca: "autocobranca",
  invisibilidade: "invisibilidade",
  rotina: "rotina",
  leveza: "leveza",
  // Common variations / typos
  exaustão: "exaustao",
  comparação: "comparacao",
  autocobrança: "autocobranca",
  "auto-cobranca": "autocobranca",
  "culpa-materna": "culpa",
  "perda-identidade": "identidade",
};

/**
 * Resolve a raw param value to a canonical AngleSlug, or null.
 */
function resolveAngle(raw: string | null | undefined): AngleSlug | null {
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase();
  return ANGLE_ALIASES[normalized] ?? null;
}

/**
 * Normaliza um valor de criativo qualquer para etiqueta usável em relatório
 * (05/09/2026). Antes, `utm_content` fora do vocabulário dos 9 ângulos era
 * descartado: `getAngleLabel` devolvia "unknown" e o cadastro nascia sem
 * ângulo, então teste A/B de criativo com rótulo próprio (`pratico`,
 * `emocional`) não conseguia ser comparado no painel. O dado sempre chegou —
 * era o rótulo que se perdia.
 */
export function slugifyAngleLabel(raw: string): string | null {
  const slug = raw
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return slug || null;
}


/**
 * Call on every landing/route entry. Reads URL params and persists attribution.
 *
 * Cookie consent (LGPD): UTM/angle são dados 1ª parte que a própria usuária
 * trouxe via URL e que serão gravados no `raw_user_meta_data` dela no signup.
 * Não é tracking 3ª parte. Por isso:
 *  - sessionStorage: gravamos SEMPRE (escopo da visita atual, atribui o signup).
 *  - localStorage: só gravamos APÓS consent (retenção cross-session).
 *
 * Forward-only: nunca sobrescreve atribuição já capturada (first-touch wins).
 */
export function captureUtmParams(searchParams: URLSearchParams): void {
  const consent = isCookieConsentAccepted();

  const writeBoth = (key: string, value: string) => {
    try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
    if (consent) {
      try { localStorage.setItem(key, value); } catch { /* ignore */ }
    }
  };

  const readPersisted = (key: string): string | null => {
    try {
      return localStorage.getItem(key) ?? sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  // Resolve angle from ?angle=, ?utm_content= ou ?utm_campaign=
  // (o criativo às vezes carrega o ângulo no nome da campanha).
  const angleParam = searchParams.get("angle");
  const utmContent = searchParams.get("utm_content");
  const utmCampaignRaw = searchParams.get("utm_campaign");
  const angle =
    resolveAngle(angleParam) ?? resolveAngle(utmContent) ?? resolveAngle(utmCampaignRaw);

  // First-touch vence — MAS "organic" é placeholder, não atribuição:
  // um ângulo real pode substituí-lo. Antes, uma visita sem parâmetro
  // gravava "organic" e travava o ângulo do anúncio que vinha depois.
  //
  // Segunda regra (25/08/2026): visita COM utm_* mas sem ângulo reconhecível
  // não é orgânica — é campanha sem ângulo no criativo. Nesse caso gravamos um
  // fallback legível (`utm_source` ou "paid"), para o criativo não sumir do
  // relatório de ângulo sem rastro.
  //
  // Terceira regra (auditoria 25/08/2026): NUNCA gravar "organic" quando já
  // existe UTM guardada (`m360:utm_params` ou `m360_utm`). A pessoa que voltou
  // digitando o endereço depois de clicar num anúncio não é orgânica — antes,
  // esse retorno etiquetava aquisição paga como orgânica.
  const hasAnyUtmParam = UTM_KEYS.some((k) => !!searchParams.get(k));
  const persistedAngle = readPersisted(SOURCE_ANGLE_KEY);
  const hasStoredUtm = !!(readPersisted(UTM_PARAMS_KEY) || readPersisted(VISITOR_UTM_KEY));
  if (angle) {
    if (!persistedAngle || persistedAngle === "organic") {
      writeBoth(SOURCE_ANGLE_KEY, angle);
    }
  } else if (hasAnyUtmParam) {
    // Campanha sem ângulo canônico: o rótulo do criativo (`utm_content`, e
    // depois `utm_term`) vira a etiqueta — é o que permite comparar variações
    // com nome próprio. Só cai na fonte quando não há criativo nenhum.
    if (!persistedAngle || persistedAngle === "organic") {
      const medium = (searchParams.get("utm_medium") || "").toLowerCase();
      const source = (searchParams.get("utm_source") || "").toLowerCase();
      const creative =
        slugifyAngleLabel(utmContent || "") ??
        slugifyAngleLabel(searchParams.get("utm_term") || "");
      const fallback = creative || source || (medium === "paid" ? "paid" : medium) || "unknown";
      writeBoth(SOURCE_ANGLE_KEY, fallback);
    }

  } else if (!persistedAngle && !hasStoredUtm) {
    writeBoth(SOURCE_ANGLE_KEY, "organic");
  }




  // Capture full UTM params if any exist.
  //
  // Última-toque, com TTL de 30 dias — igual a `m360_utm` do visitorTracker.
  // Antes era first-touch travado (`!readPersisted(...)`), e isso fazia as duas
  // chaves divergirem: `m360_utm` atualizava na visita nova e `m360:utm_params`
  // ficava com o valor antigo para sempre. Quem alimenta o cadastro é ESTA
  // chave (`getUtmParams` → Auth.tsx e useAttributionRecorder), então o
  // travamento contaminava a atribuição da conta com a campanha errada.
  const utmData: Record<string, string> = {};
  let hasUtm = false;

  for (const key of UTM_KEYS) {
    const val = searchParams.get(key);
    if (val) {
      utmData[key] = val;
      hasUtm = true;
    }
  }

  if (hasUtm) {
    writeBoth(UTM_PARAMS_KEY, JSON.stringify({ ...utmData, ts: Date.now() }));
  }

  // Capture fbclid (Meta click ID).
  //
  // Também última-toque + TTL (`FBCLID_TTL_MS`). Era first-touch sem validade:
  // um fbclid antigo (inclusive de teste manual) ficava gravado no localStorage
  // e era anexado a TODO evento do Pixel/CAPI dali para frente, contaminando a
  // atribuição inteira. Clique novo sempre substitui; clique velho expira.
  const fbclid = searchParams.get("fbclid")?.trim();
  if (fbclid) {
    writeBoth(FBCLID_KEY, fbclid);
    writeBoth(FBCLID_TS_KEY, String(Date.now()));
  }

  // Capture gclid / gbraid / wbraid (Google Ads click ID) — 03/09/2026.
  //
  // Campanha do Google Ads NÃO manda utm_* por padrão: manda `gclid`, e nos
  // caminhos com restrição de rastreamento manda `gbraid` (app/iOS) ou `wbraid`
  // (web). Sem ler isso, a única campanha paga rodando era invisível na
  // atribuição — 141 instalações caíram como `google-play/organic`.
  //
  // Mesmo desenho do fbclid: última-toque + TTL de 90 dias (janela padrão de
  // conversão do Google Ads).
  for (const key of GCLID_KEYS) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      writeBoth(GCLID_KEY, value.slice(0, 200));
      writeBoth(GCLID_SOURCE_KEY, key);
      writeBoth(GCLID_TS_KEY, String(Date.now()));
      break;
    }
  }


  // Capture additional origin params (param, content_id, campaign, referrer domain)
  const originKeys = ["param", "content_id", "campaign"] as const;
  const originData: Record<string, string> = {};
  let hasOrigin = false;

  for (const key of originKeys) {
    const val = searchParams.get(key)?.trim().toLowerCase();
    if (val) {
      originData[key] = val;
      hasOrigin = true;
    }
  }

  // Se a URL veio só com UTM padrão, ainda guardamos um rastro de origem para
  // debug/auditoria. A atribuição oficial segue nas chaves `utm_*`; isto evita
  // `m360:origin_params = null` em campanha real sem `param`/`content_id`.
  if (!originData.campaign && utmCampaignRaw) {
    originData.campaign = utmCampaignRaw.trim().toLowerCase();
    hasOrigin = true;
  }

  // Capture referrer domain (no full URL for privacy)
  try {
    const ref = document.referrer;
    if (ref) {
      const refDomain = new URL(ref).hostname;
      if (refDomain && refDomain !== window.location.hostname) {
        originData.referrer = refDomain;
        hasOrigin = true;
      }
    }
  } catch { /* ignore */ }

  if (hasOrigin && !readPersisted(ORIGIN_PARAMS_KEY)) {
    originData.captured_at = new Date().toISOString();
    writeBoth(ORIGIN_PARAMS_KEY, JSON.stringify(originData));
  }
}

/** Helper que lê localStorage com fallback pra sessionStorage. */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Get the stored source angle slug. Returns AngleSlug | "organic" | null.
 */
export function getSourceAngle(): string | null {
  const stored = readStored(SOURCE_ANGLE_KEY);
  if (stored) return stored;

  // Fallback de compatibilidade: `usePageView` captura UTMs globais em
  // `m360_utm`. Se a usuária entra direto em /auth ou alguma landing futura não
  // chama `captureUtmParams`, o cadastro ainda consegue derivar o ângulo.
  const utm = getUtmParams();
  return resolveAngle(utm?.utm_content) ?? resolveAngle(utm?.utm_campaign);
}

/** Janela de validade dos UTMs (mesma do `m360_utm` do visitorTracker). */
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Janela de atribuição de clique do Meta. Depois disso o fbclid é ruído. */
const FBCLID_TTL_MS = 28 * 24 * 60 * 60 * 1000;

function removeStored(key: string): void {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function parseStoredUtm(raw: string | null, keyToRemoveOnExpiry: string): Record<string, string> | null {
  if (!raw) return null;
  const parsed = JSON.parse(raw) as Record<string, string | number>;
  const ts = typeof parsed.ts === "number" ? parsed.ts : null;
  if (ts && Date.now() - ts > UTM_TTL_MS) {
    removeStored(keyToRemoveOnExpiry);
    return null;
  }
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = parsed[key];
    if (typeof value === "string" && value) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Get stored UTM params as object, or null.
 * Expira em 30 dias — e nunca devolve o `ts` interno para o consumidor
 * (o objeto vai direto para o `raw_user_meta_data` do cadastro).
 */
export function getUtmParams(): Record<string, string> | null {
  try {
    return (
      parseStoredUtm(readStored(UTM_PARAMS_KEY), UTM_PARAMS_KEY) ??
      parseStoredUtm(readStored(VISITOR_UTM_KEY), VISITOR_UTM_KEY)
    );
  } catch {
    return null;
  }
}

/**
 * Get stored origin params (param, content_id, campaign, referrer), or null.
 */
export function getOriginParams(): Record<string, string> | null {
  try {
    const raw = readStored(ORIGIN_PARAMS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Get stored fbclid (Meta click ID), or null.
 * Expira em 28 dias: fbclid velho anexado a todo evento é atribuição falsa.
 */
export function getFbclid(): string | null {
  const value = readStored(FBCLID_KEY);
  if (!value) return null;
  const ts = getFbclidTimestamp();
  if (ts && Date.now() - ts > FBCLID_TTL_MS) {
    removeStored(FBCLID_KEY);
    removeStored(FBCLID_TS_KEY);
    return null;
  }
  return value;
}


/**
 * Get the timestamp (ms) of the first fbclid capture. Used to build
 * the Meta `_fbc` parameter format: `fb.1.<timestamp_ms>.<fbclid>`.
 */
export function getFbclidTimestamp(): number | null {
  const raw = readStored(FBCLID_TS_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Janela de conversão do Google Ads. Depois disso o clique é ruído. */
const GCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Clique do Google Ads guardado, ou null. Expira em 90 dias.
 *
 * LGPD: é pseudônimo (identifica um clique, não uma pessoa), mas passa a ser
 * dado pessoal ao ser vinculado à conta em `user_attribution` — mesma classe do
 * `fbclid`. Apagado com a conta junto da linha de atribuição.
 */
export function getGclid(): { value: string; source: string } | null {
  const value = readStored(GCLID_KEY);
  if (!value) return null;
  const raw = readStored(GCLID_TS_KEY);
  const ts = raw ? Number(raw) : null;
  if (ts && Number.isFinite(ts) && Date.now() - ts > GCLID_TTL_MS) {
    removeStored(GCLID_KEY);
    removeStored(GCLID_SOURCE_KEY);
    removeStored(GCLID_TS_KEY);
    return null;
  }
  const source = readStored(GCLID_SOURCE_KEY);
  return { value, source: source && GCLID_KEYS.includes(source as never) ? source : "gclid" };
}




/**
 * Get angle label for readability in queries.
 * Now returns the canonical slug directly (which is already readable).
 */
export function getAngleLabel(angle: string | null): string {
  if (!angle || angle === "organic") return angle ?? "unknown";
  // Check if it's a canonical slug
  if (ANGLE_LABELS[angle as AngleSlug]) return angle;
  // Legacy: resolve aliases
  const resolved = ANGLE_ALIASES[angle];
  if (resolved) return resolved;
  // Rótulo livre de criativo: preserva em vez de virar "unknown" (05/09/2026).
  return slugifyAngleLabel(angle) ?? "unknown";
}


/* ── Campaign URL builder (for admin reference) ── */

const SITE_URL = "https://materna360.com.br";

export interface CampaignUrlParams {
  angle: AngleSlug;
  source: string;    // instagram, facebook, google
  medium: string;    // cpc, cpm, social, email
  campaign: string;  // campaign name
  term?: string;     // keyword (Google Ads)
}

/**
 * Build a fully attributed campaign URL.
 */
export function buildCampaignUrl(params: CampaignUrlParams): string {
  const url = new URL(SITE_URL);
  url.searchParams.set("utm_source", params.source);
  url.searchParams.set("utm_medium", params.medium);
  url.searchParams.set("utm_campaign", params.campaign);
  url.searchParams.set("utm_content", params.angle);
  if (params.term) url.searchParams.set("utm_term", params.term);
  url.searchParams.set("angle", params.angle);
  return url.toString();
}

/**
 * Generate all test campaign URLs for validation.
 */
export function generateTestCampaignUrls(): Record<AngleSlug, string> {
  const urls = {} as Record<AngleSlug, string>;
  for (const angle of ANGLE_SLUGS) {
    urls[angle] = buildCampaignUrl({
      angle,
      source: "meta",
      medium: "cpc",
      campaign: `test_${angle}_v1`,
    });
  }
  return urls;
}
